// Frontend Analytics Tracking Service
// This ensures analytics work in production where static pages are served by Apache

interface PageViewData {
  page_url: string;
  page_title: string;
  referrer?: string;
  user_agent?: string;
}

interface EventData {
  event_type: string;
  event_data?: any;
  page_url?: string;
}

class AnalyticsService {
  private apiBaseUrl: string;
  private sessionId: string | null = null;
  private initialized: boolean = false;
  private initializing: boolean = false;

  constructor() {
    this.apiBaseUrl = this.getApiBaseUrl();
    // Don't auto-initialize in constructor to prevent loops
  }

  private getApiBaseUrl(): string {
    return process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';
  }

  private async initializeSession(): Promise<void> {
    if (this.initialized || this.initializing) {
      return;
    }
    
    this.initializing = true;
    
    try {
      // Check if we already have a session cookie
      const cookies = document.cookie.split(';');
      const sessionCookie = cookies.find(c => c.trim().startsWith('analytics_session='));
      
      if (sessionCookie) {
        this.sessionId = sessionCookie.split('=')[1].trim();
        this.initialized = true;
        this.initializing = false;
        console.log('Found existing analytics session:', this.sessionId);
        return;
      }

      console.log('No existing session found, creating new session...');
      
      // Create a new session by making a request to the backend
      const response = await fetch(`${this.apiBaseUrl}/analytics/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Include cookies
        body: JSON.stringify({
          user_agent: navigator.userAgent,
          referrer: document.referrer,
          timestamp: new Date().toISOString()
        })
      });

      if (response.ok) {
        const data = await response.json();
        this.sessionId = data.sessionId;
        this.initialized = true;
        console.log('Created new analytics session:', this.sessionId);
      } else {
        console.warn('Failed to create analytics session:', response.status);
      }
    } catch (error) {
      console.warn('Analytics initialization failed:', error);
      // Continue without analytics rather than breaking the site
    } finally {
      this.initializing = false;
    }
  }

  public async trackPageView(additionalData?: Partial<PageViewData>): Promise<void> {
    try {
      await this.initializeSession();
      
      if (!this.initialized || !this.sessionId) {
        console.warn('[Frontend Analytics] Session not initialized, skipping page view tracking');
        return;
      }

      const pageViewData: PageViewData = {
        page_url: window.location.pathname + window.location.search,
        page_title: document.title,
        referrer: document.referrer,
        user_agent: navigator.userAgent,
        ...additionalData
      };

      console.log('[Frontend Analytics] Tracking page view:', pageViewData);

      const response = await fetch(`${this.apiBaseUrl}/analytics/pageview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(pageViewData)
      });

      if (response.ok) {
        console.log('[Frontend Analytics] Page view tracked successfully');
      } else {
        console.warn('[Frontend Analytics] Page view tracking failed:', response.status, response.statusText);
      }
    } catch (error) {
      console.warn('Failed to track page view:', error);
    }
  }

  public async trackEvent(eventType: string, eventData?: any): Promise<void> {
    try {
      await this.initializeSession();
      
      if (!this.initialized || !this.sessionId) {
        console.warn('[Frontend Analytics] Session not initialized, skipping event tracking');
        return;
      }

      const event: EventData = {
        event_type: eventType,
        event_data: eventData,
        page_url: window.location.pathname + window.location.search
      };

      await fetch(`${this.apiBaseUrl}/analytics/event`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(event)
      });
    } catch (error) {
      console.warn('Failed to track event:', error);
    }
  }

  // Track page view automatically when the URL changes (for SPAs)
  public startAutoTracking(): void {
    // Track initial page view
    this.trackPageView();

    // Track page views on navigation (for React Router)
    let currentPath = window.location.pathname;
    
    const checkForNavigation = () => {
      const newPath = window.location.pathname;
      if (newPath !== currentPath) {
        currentPath = newPath;
        this.trackPageView();
      }
    };

    // Check for navigation changes every 500ms
    setInterval(checkForNavigation, 500);

    // Also listen for popstate events (back/forward navigation)
    window.addEventListener('popstate', () => {
      setTimeout(() => this.trackPageView(), 100);
    });

    // Update session duration every 30 seconds
    setInterval(() => {
      this.updateSessionDuration();
    }, 30 * 1000);

    // Update session duration when page becomes hidden/visible
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.updateSessionDuration();
      }
    });

    // Update session duration before page unload
    window.addEventListener('beforeunload', () => {
      this.updateSessionDuration();
    });
  }

  // Update session duration
  private async updateSessionDuration(): Promise<void> {
    if (!this.initialized || !this.sessionId) return;

    try {
      await fetch(`${this.apiBaseUrl}/analytics/duration`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          timestamp: new Date().toISOString()
        })
      });
    } catch (error) {
      // Silently fail - don't spam console for duration updates
    }
  }
}

// Create a singleton instance
const analytics = new AnalyticsService();

export default analytics;